import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { SignupForm, FormSubmission } from '../types/signupForm';
//its a temporary fix for the error of save dr
const stripUndefined = (obj: any): any => JSON.parse(JSON.stringify(obj));

class SignupFormService {
  private readonly COLLECTION_NAME = 'signupForms';
  private readonly SUBMISSIONS_COLLECTION = 'formSubmissions';

  // Generate a unique short form ID
  private generateFormId(): string {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    const randomValues = new Uint32Array(10);
    window.crypto.getRandomValues(randomValues);
    for (let i = 0; i < 10; i++) {
      result += chars[randomValues[i] % chars.length];
    }
    return result;
  }

  // Create a new signup form
  async createForm(
    userId: string,
    workspaceId: string,
    formData: Omit<SignupForm, 'id' | 'userId' | 'workspaceId' | 'formId' | 'createdAt' | 'updatedAt' | 'analytics'>,
    createdBy?: string
  ): Promise<string> {
    try {
      const formRef = collection(db, this.COLLECTION_NAME);
      const formId = this.generateFormId();

      const newForm: Omit<SignupForm, 'id'> = {
        ...stripUndefined(formData),
        userId,
        workspaceId,
        formId,
        createdBy: createdBy || userId, // Use provided createdBy or default to userId
        isTemplate: createdBy === 'admin', // Admin forms are templates by default
        analytics: {
          views: 0,
          submissions: 0,
          conversionRate: 0
        },
        createdAt: serverTimestamp() as Timestamp,
        updatedAt: serverTimestamp() as Timestamp
      };

      const docRef = await addDoc(formRef, newForm);
      return docRef.id;
    } catch (error) {
      console.error('Error creating signup form:', error);
      throw new Error('Failed to create signup form');
    }
  }

  // Get a form by ID
  async getForm(formId: string): Promise<SignupForm | null> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, formId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() } as SignupForm;
      }
      return null;
    } catch (error) {
      console.error('Error getting signup form:', error);
      throw new Error('Failed to get signup form');
    }
  }

  // Get form by short formId (for public access)
  async getFormByShortId(shortFormId: string): Promise<SignupForm | null> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('formId', '==', shortFormId),
        where('isPublished', '==', true),
        where('isActive', '==', true)
      );

      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return { id: doc.id, ...doc.data() } as SignupForm;
      }
      return null;
    } catch (error) {
      console.error('Error getting form by short ID:', error);
      throw new Error('Failed to get form');
    }
  }

  // Get all forms for a workspace
  async getForms(userId: string, workspaceId: string): Promise<SignupForm[]> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('userId', '==', userId),
        where('workspaceId', '==', workspaceId),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as SignupForm[];
    } catch (error) {
      console.error('Error getting signup forms:', error);
      throw new Error('Failed to get signup forms');
    }
  }

  // Get all forms (for admin)
  async getAllForms(): Promise<SignupForm[]> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as SignupForm[];
    } catch (error) {
      console.error('Error getting all signup forms:', error);
      throw new Error('Failed to get all signup forms');
    }
  }

  // Get admin-created form templates (available to all users)
  async getAdminTemplates(): Promise<SignupForm[]> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('createdBy', '==', 'admin'),
        where('isTemplate', '==', true),
        where('isActive', '==', true),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as SignupForm[];
    } catch (error) {
      console.error('Error getting admin signup form templates:', error);
      throw new Error('Failed to get admin templates');
    }
  }

  // Update a form
  async updateForm(formId: string, updates: Partial<SignupForm>): Promise<void> {
    try {
      await updateDoc(doc(db, this.COLLECTION_NAME, formId), {
        ...stripUndefined(updates),
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error updating signup form:', error);
      throw new Error('Failed to update signup form');
    }
  }

  // Delete a form
  async deleteForm(formId: string): Promise<void> {
    try {
      await deleteDoc(doc(db, this.COLLECTION_NAME, formId));
    } catch (error) {
      console.error('Error deleting signup form:', error);
      throw new Error('Failed to delete signup form');
    }
  }

  // Publish/unpublish a form
  async publishForm(formId: string, publish: boolean): Promise<void> {
    try {
      const updateData: any = {
        isPublished: publish,
        isActive: publish,
        updatedAt: serverTimestamp()
      };

      if (publish) {
        updateData.publishedAt = serverTimestamp();
      }

      await updateDoc(doc(db, this.COLLECTION_NAME, formId), updateData);

      // Shopify auto-popup: fire-and-forget. We don't await this so publish is fast and resilient.
      this.getForm(formId).then(form => {
        if (form?.workspaceId) {
          const fn = httpsCallable(functions, 'syncShopifyOnsiteSignupForm');

          if (publish) {
            fn({
              workspaceId: form.workspaceId,
              enabled: true,
              shortFormId: form.formId,
              formType: form.type === 'popup' || form.type === 'slidein' || form.type === 'bar' ? form.type : 'popup',
              popupType: form.settings?.popupType || null,
              delayMs: 300,
            }).catch(e => console.error('[SignupForm] Shopify onsite sync failed:', e));
          } else {
            fn({
              workspaceId: form.workspaceId,
              enabled: false,
            }).catch(e => console.error('[SignupForm] Shopify onsite disable failed:', e));
          }
        }
      }).catch(e => console.error('[SignupForm] Failed to load form for Shopify sync:', e));
    } catch (error) {
      console.error('Error publishing signup form:', error);
      throw new Error(
        error instanceof Error && error.message
          ? error.message
          : 'Failed to publish signup form'
      );
    }
  }

  // Track form view (server callable; anonymous users cannot write analytics on the client)
  async trackFormView(shortFormId: string): Promise<void> {
    try {
      const trackFn = httpsCallable(functions, 'trackSignupFormViewPublic');
      await trackFn({ shortFormId });
    } catch (error) {
      console.error('Error tracking form view:', error);
    }
  }

  // Submit form and create contact (server callable with Admin SDK)
  async submitForm(
    shortFormId: string,
    formData: { [fieldId: string]: any },
    metadata?: {
      source?: string;
      userAgent?: string;
      ipAddress?: string;
    },
    honeypot?: string
  ): Promise<{ success: boolean; contactId?: string; message?: string; requiresConfirmation?: boolean }> {
    try {
      const submitFn = httpsCallable(functions, 'submitSignupFormPublic');
      const result = await submitFn({
        shortFormId,
        formData,
        metadata: metadata || {},
        honeypot: honeypot || '',
      });
      const payload = result.data as {
        success?: boolean;
        contactId?: string;
        message?: string;
        requiresConfirmation?: boolean;
      };
      return {
        success: !!payload.success,
        contactId: payload.contactId,
        message: payload.message,
        requiresConfirmation: payload.requiresConfirmation,
      };
    } catch (error: any) {
      console.error('Error submitting form:', error);
      const msg =
        typeof error?.message === 'string' && error.message.includes('INTERNAL')
          ? 'Failed to submit form. Please try again.'
          : error?.message || 'Failed to submit form. Please try again.';
      return { success: false, message: msg };
    }
  }

  // Recalculate conversion rate
  private async recalculateConversionRate(formId: string): Promise<void> {
    try {
      const form = await this.getForm(formId);
      if (!form) return;

      const conversionRate = form.analytics.views > 0
        ? (form.analytics.submissions / form.analytics.views) * 100
        : 0;

      await updateDoc(doc(db, this.COLLECTION_NAME, formId), {
        'analytics.conversionRate': conversionRate
      });
    } catch (error) {
      console.error('Error recalculating conversion rate:', error);
    }
  }

  // Get form submissions
  async getFormSubmissions(formId: string): Promise<FormSubmission[]> {
    try {
      const q = query(
        collection(db, this.SUBMISSIONS_COLLECTION),
        where('formId', '==', formId),
        orderBy('submittedAt', 'desc'),
        limit(100)
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as FormSubmission[];
    } catch (error) {
      console.error('Error getting form submissions:', error);
      throw new Error('Failed to get form submissions');
    }
  }

  // Generate embed code
  generateEmbedCode(shortFormId: string, domain: string = window.location.origin): string {
    return `<script src="${domain}/embed/signup-form.js" data-form-id="${shortFormId}"></script>`;
  }

  // Generate iframe embed code
  generateIframeCode(shortFormId: string, domain: string = window.location.origin): string {
    return `<iframe src="${domain}/form/${shortFormId}" width="100%" height="400" frameborder="0"></iframe>`;
  }
}

export const signupFormService = new SignupFormService();

